import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

const STORAGE_KEY = 'kunde_token';
const LOGOUT_KEY = 'kunde_logout';
const WINDOW_STORAGE_PREFIX = 'kunde_window:';

export interface KundeTokenPayload {
  externekundenid: string;
  taskid: string;
  authtime: number;
  iat: number;
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
}

export interface KundeTokenDisplayInfo {
  vorname: string;
  name: string;
  email?: string;
  mlpKundennummer?: string;
}

interface KundeTokenStorage {
  token: string;
  gueltigBis: string;
  vorname?: string;
  name?: string;
  email?: string;
  mlpKundennummer?: string;
}

@Injectable({ providedIn: 'root' })
export class KundeTokenService {
  private readonly _token = signal<string | null>(null);
  private readonly _gueltigBis = signal<string | null>(null);
  private readonly _vorname = signal<string | null>(null);
  private readonly _name = signal<string | null>(null);
  private readonly _email = signal<string | null>(null);
  private readonly _mlpKundennummer = signal<string | null>(null);
  private readonly _windowLink = signal<string | null>(null);
  private readonly _berater = signal(false);
  private readonly _loggedOutElsewhere = new Subject<string | null>();

  readonly loggedOutElsewhere$: Observable<string | null> = this._loggedOutElsewhere.asObservable();
  readonly windowMode: Signal<boolean> = computed(() => this._windowLink() !== null);
  readonly staffMode: Signal<boolean> = computed(() => this.windowMode() || this._berater());
  readonly email: Signal<string | null> = this._email.asReadonly();
  readonly vorname: Signal<string | null> = this._vorname.asReadonly();
  readonly nachname: Signal<string | null> = this._name.asReadonly();

  readonly isLoggedIn: Signal<boolean> = computed(() => {
    const token = this._token();
    if (!token) return false;
    const gueltigBis = this._gueltigBis();
    if (!gueltigBis) return false;
    return new Date(gueltigBis).getTime() > Date.now();
  });

  readonly displayName: Signal<string | null> = computed(() => {
    const vorname = this._vorname();
    const name = this._name();
    if (!vorname && !name) return null;
    return [vorname, name].filter(Boolean).join(' ');
  });

  setToken(token: string, gueltigBis: string, displayInfo?: KundeTokenDisplayInfo): void {
    this._token.set(token);
    this._gueltigBis.set(gueltigBis);
    this._vorname.set(displayInfo?.vorname ?? null);
    this._name.set(displayInfo?.name ?? null);
    this._email.set(displayInfo?.email ?? null);
    this._mlpKundennummer.set(displayInfo?.mlpKundennummer ?? null);
    this.persist();
  }

  refreshToken(token: string, gueltigBis: string): void {
    if (!this._token()) return;
    if (!this.belongsToCurrentKunde(token)) return;
    const current = this._gueltigBis();
    if (current && new Date(gueltigBis) <= new Date(current)) return;
    this._token.set(token);
    this._gueltigBis.set(gueltigBis);
    this.persist();
  }

  // Through getToken(), not isLoggedIn: that computed is memoized and keeps an expired session's id.
  externeKundenId(): string | null {
    const token = this.getToken();
    return token ? (decodeKundeTokenPayload(token)?.externekundenid ?? null) : null;
  }

  mlpKundennummer(): string | null {
    return this.getToken() ? this._mlpKundennummer() : null;
  }

  getToken(): string | null {
    const token = this._token();
    if (!token) return null;

    if (this.remainingMs() === 0) {
      this.clear();
      return null;
    }

    return token;
  }

  clear(): void {
    const own = this._token();
    this.resetSession();
    const windowLink = this._windowLink();
    if (windowLink !== null) {
      if (own && this.readWindowEntry(windowLink)?.token === own)
        this.removeWindowEntry(windowLink);
      return;
    }
    if (own && this.parseStored(localStorage.getItem(STORAGE_KEY))?.token === own) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  logout(): void {
    if (this._windowLink() !== null) {
      this.clear();
      return;
    }
    this.resetSession();
    try {
      localStorage.setItem(LOGOUT_KEY, String(Date.now()));
      localStorage.removeItem(LOGOUT_KEY);
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  }

  markBerater(isBerater: boolean): void {
    this._berater.set(isBerater);
  }

  enterWindow(linkToken: string): void {
    this._windowLink.set(linkToken);
    this.resetSession();
    this.pruneWindowEntries();
    const stored = this.readWindowEntry(linkToken);
    if (stored) this.applyStored(stored);
  }

  bootstrapFromStorage(): void {
    const stored = this.readFromStorage();
    if (stored) {
      this.applyStored(stored);
    }

    window.addEventListener('storage', e => {
      if (this._windowLink() !== null) return;
      if (e.key === LOGOUT_KEY) {
        if (e.newValue) this.endLoggedOutSession();
        return;
      }
      if (e.key !== STORAGE_KEY) return;
      if (!e.newValue) {
        this.resetSession();
        return;
      }
      const updated = this.parseStored(e.newValue);
      if (!updated) return;
      if (this._token() && !this.belongsToCurrentKunde(updated.token)) {
        this.endLoggedOutSession();
        return;
      }
      this.applyStored(updated);
    });
  }

  private endLoggedOutSession(): void {
    const token = this._token();
    if (!token) return;
    this.resetSession();
    this._loggedOutElsewhere.next(decodeKundeTokenPayload(token)?.externekundenid ?? null);
  }

  private applyStored(stored: KundeTokenStorage): void {
    this._token.set(stored.token);
    this._gueltigBis.set(stored.gueltigBis);
    this._vorname.set(stored.vorname ?? null);
    this._name.set(stored.name ?? null);
    this._email.set(stored.email ?? null);
    this._mlpKundennummer.set(stored.mlpKundennummer ?? null);
  }

  private resetSession(): void {
    this._token.set(null);
    this._gueltigBis.set(null);
    this._vorname.set(null);
    this._name.set(null);
    this._email.set(null);
    this._mlpKundennummer.set(null);
  }

  private belongsToCurrentKunde(token: string): boolean {
    const currentKunde = decodeKundeTokenPayload(this._token() ?? '')?.externekundenid;
    const incomingKunde = decodeKundeTokenPayload(token)?.externekundenid;
    if (!currentKunde || !incomingKunde) return true;
    return currentKunde === incomingKunde;
  }

  private remainingMs(): number {
    const gueltigBis = this._gueltigBis();
    if (!gueltigBis) return 0;
    return Math.max(0, new Date(gueltigBis).getTime() - Date.now());
  }

  private persist(): void {
    const token = this._token();
    const gueltigBis = this._gueltigBis();
    if (!token || !gueltigBis) return;
    const vorname = this._vorname() ?? undefined;
    const name = this._name() ?? undefined;
    const email = this._email() ?? undefined;
    const mlpKundennummer = this._mlpKundennummer() ?? undefined;
    const entry = JSON.stringify({
      token,
      gueltigBis,
      vorname,
      name,
      email,
      mlpKundennummer,
    });
    const windowLink = this._windowLink();
    if (windowLink !== null) {
      this.writeWindowEntry(windowLink, entry);
      return;
    }
    localStorage.setItem(STORAGE_KEY, entry);
  }

  private readWindowEntry(linkToken: string): KundeTokenStorage | null {
    try {
      return this.parseStored(sessionStorage.getItem(WINDOW_STORAGE_PREFIX + linkToken));
    } catch {
      return null;
    }
  }

  private writeWindowEntry(linkToken: string, entry: string): void {
    try {
      sessionStorage.setItem(WINDOW_STORAGE_PREFIX + linkToken, entry);
    } catch {
      return;
    }
  }

  private removeWindowEntry(linkToken: string): void {
    try {
      sessionStorage.removeItem(WINDOW_STORAGE_PREFIX + linkToken);
    } catch {
      return;
    }
  }

  private pruneWindowEntries(): void {
    try {
      const now = new Date();
      const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
      for (const key of keys) {
        if (!key?.startsWith(WINDOW_STORAGE_PREFIX)) continue;
        const stored = this.parseStored(sessionStorage.getItem(key));
        if (!stored || new Date(stored.gueltigBis) <= now) sessionStorage.removeItem(key);
      }
    } catch {
      return;
    }
  }

  private readFromStorage(): KundeTokenStorage | null {
    const stored = this.parseStored(localStorage.getItem(STORAGE_KEY));
    if (!stored) return null;

    if (stored.gueltigBis && new Date(stored.gueltigBis) <= new Date()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return stored;
  }

  private parseStored(raw: string | null): KundeTokenStorage | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as KundeTokenStorage;
      return parsed.token ? parsed : null;
    } catch {
      return null;
    }
  }
}

export function decodeKundeTokenPayload(jwt: string): KundeTokenPayload | null {
  try {
    const segment = jwt.split('.')[1];
    if (!segment) return null;
    return JSON.parse(atob(segment)) as KundeTokenPayload;
  } catch {
    return null;
  }
}

export function decodeJwtExp(jwt: string): string | null {
  const payload = decodeKundeTokenPayload(jwt);
  if (!payload || typeof payload.exp !== 'number') return null;
  return new Date(payload.exp * 1000).toISOString();
}

export function initKundeToken(): () => void {
  return () => {
    inject(KundeTokenService).bootstrapFromStorage();
  };
}
